import { Injectable, OnDestroy } from '@angular/core';
import { Person } from '../models/person';
import { CommunicationService } from './communication.service';
import { AwallComponentEvent } from '../models/awallComponentEvent';
import { SynchronizationService } from './synchronization.service';
import { Subscription } from 'rxjs';
import { ComponentEvent } from '../models/enums/componentEvent';
import { SyncEventType } from '../models/enums/syncEventType';
import { EventTeammemberHighlightChanged } from '../models/syncEvents/realtimeEvents/eventTeammemberHightlightChanged';

/**
 * This service is used to help with highlighting teammembers and cards where a specific teammember is the assignee
 * with a specific color
 */
@Injectable({
  providedIn: 'root',
  })
export class HighlightTeammemberService implements OnDestroy {

  private subscriptions: Subscription[] = [];
  private activeHighlights: Map<string, string> = new Map();
  private highlightColorNames: string[] =
  ['highlight-red', 'highlight-green', 'highlight-blue', 'highlight-purple', 'highlight-gold'];
  private highlightColorCurrentIndex = 0;


  constructor(
    private communicationService: CommunicationService,
    private syncService: SynchronizationService) {
    this.initHighlightEventSubscription();
  }

  ngOnDestroy(): void {
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }
  }

  private initHighlightEventSubscription(): void {
    this.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.WIDGET_TEAM_HIGHTLIGHT_CHANGED).subscribe(data => {
      if (data.active) {
        this.addHighlighting(data.assignee);
      } else {
        this.removeHighlighting(data.assignee);
      }
    }));
  }

  /**
   * @param assignee the assignee whos tasks/userstories should be (un-)highlighted
   * @returns active returns the new state of the hightlighting (true = active, false = inactive)
   */
  public toggleHighlighting(assignee: Person): void {
    if (this.activeHighlights.has(assignee.id)) {
      this.removeHighlighting(assignee);
      const event = new EventTeammemberHighlightChanged(assignee, false);
      this.syncService.sendRealTimeEventNew(event);
    } else {
      this.addHighlighting(assignee);
      const event = new EventTeammemberHighlightChanged(assignee, true);
      this.syncService.sendRealTimeEventNew(event);
    }
  }

  private addHighlighting(assignee: Person): void {
    if (!this.activeHighlights.has(assignee.id)) {
      const colorName = this.getNextHighlightColorName();
      this.activeHighlights.set(assignee.id, colorName);
      this.sendChangeEvent(assignee, colorName);
    }
  }

  private removeHighlighting(assignee: Person): void {
    this.activeHighlights.delete(assignee.id);
    this.sendChangeEvent(assignee, null);
  }

  private getNextHighlightColorName(): string {
    const name = this.highlightColorNames[this.highlightColorCurrentIndex];
    this.highlightColorCurrentIndex++;
    if (this.highlightColorCurrentIndex >= this.highlightColorNames.length) {
      this.highlightColorCurrentIndex = 0;
    }
    return name;
  }

  /**
   * Inform all registered Components of communicationService that highlighting has changed
   */
  private sendChangeEvent(assignee: Person, colorName: string): void {
    const data = { 'person': assignee, 'colorName': colorName };
    const event = new AwallComponentEvent(ComponentEvent.WIDGET_TEAM_HIGHLIGHT_CHANGED, data);
    this.communicationService.sendToAll(event);
  }


  public getHighlightColorName(assignee: Person): string {
    if (assignee && this.activeHighlights.has(assignee.id)) {
      return this.activeHighlights.get(assignee.id);
    } else {
      return '';
    }
  }


}
